import type {
  GetPricingResponse,
  UpsertPricingRequest,
  UpsertPricingResponse,
} from '@nadoutong/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { opsApiRequest } from './client.ts';

/**
 * Typed wrappers + React Query hooks for the `/v1/ops/pricing`
 * surface.
 *
 *   - `GET /v1/ops/pricing` — read current `USD_CNY` policy + latest
 *     market sample (epic #84 PR-P4).
 *   - `POST /v1/ops/pricing` — upsert the policy rate. Server fills
 *     `updated_by` from the session cookie (no client field).
 *
 * Query key convention:
 *   - `['pricing']` — single entity (there is only one `USD_CNY` row
 *     in Phase 4). Invalidation after mutation refetches the policy
 *     + the companion market sample.
 *
 * Spoof-resistance: client NEVER sends `updated_by` in the body — it's
 * injected server-side from the ops session (`X-Ops-User-Id` header).
 * The TypeScript contract (`UpsertPricingRequest`) omits the field so
 * a typo "client did send it" can't compile.
 */

const BASE_PATH = '/v1/ops/pricing';

export const PRICING_QUERY_KEY = ['pricing'] as const;

export async function getPricing(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<GetPricingResponse> {
  return opsApiRequest<GetPricingResponse>(BASE_PATH, { signal }, fetchImpl);
}

export async function upsertPricing(
  body: UpsertPricingRequest,
  fetchImpl: typeof fetch,
): Promise<UpsertPricingResponse> {
  return opsApiRequest<UpsertPricingResponse>(
    BASE_PATH,
    { method: 'POST', body },
    fetchImpl,
  );
}

export function usePricing(): UseQueryResult<GetPricingResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: PRICING_QUERY_KEY,
    queryFn: ({ signal }) => getPricing(fetchImpl, signal),
  });
}

export function useUpsertPricing(): UseMutationResult<
  UpsertPricingResponse,
  Error,
  UpsertPricingRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => upsertPricing(body, fetchImpl),
    onSuccess: () => {
      // Single-entity invalidation — the whole `['pricing']` subtree
      // (just one row today) refetches. No partial-update: the policy
      // display includes server-generated `updated_at` + `updated_by`
      // that must come from the fresh GET, not the mutation response.
      void qc.invalidateQueries({ queryKey: PRICING_QUERY_KEY });
    },
  });
}
